import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, TextField, Snackbar, Alert, Paper, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Grid, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const PARTNER_TYPES = [
  { value: 'consultant', label: 'Consultant' },
  { value: 'partner', label: 'Partner' },
  { value: 'analyst', label: 'Analyst' },
];

const emptyForm = {
  name: '',
  type: 'consultant',
  company: '',
  contact: '',
  email: '',
  notes: ''
};

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({ q: '', type: '', company: '', from: '', to: '' });
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchPartners = () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    fetch('/api/partners?' + params.toString())
      .then(res => res.json())
      .then(data => {
        setPartners(data);
        setLoading(false);
      });
  };

  useEffect(() => { fetchPartners(); }, [filters]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };

  const handleDialogOpen = () => {
    setForm(emptyForm);
    setEditingId(null);
    setDialogOpen(true);
    setError('');
    setSuccess('');
  };
  const handleDialogClose = () => setDialogOpen(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const res = await fetch(editingId ? `/api/partners/${editingId}` : '/api/partners', {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save partner');
      setSuccess(editingId ? 'Partner updated!' : 'Partner added!');
      setForm(emptyForm);
      setEditingId(null);
      setDialogOpen(false);
      fetchPartners();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = partner => {
    setForm({
      name: partner.name,
      type: partner.type,
      company: partner.company,
      contact: partner.contact,
      email: partner.email,
      notes: partner.notes || ''
    });
    setEditingId(partner.id);
    setDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this partner?')) return;
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`/api/partners/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete partner');
      setSuccess('Partner deleted!');
      fetchPartners();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Partners</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleDialogOpen}>
          Add Partner
        </Button>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Filter Partners</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField label="Keyword" name="q" value={filters.q} onChange={handleFilterChange} size="small" />
              <TextField label="Type" name="type" value={filters.type} onChange={handleFilterChange} size="small" select sx={{ minWidth: 120 }}>
                <MenuItem value="">Type</MenuItem>
                {PARTNER_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
              <TextField label="Company" name="company" value={filters.company} onChange={handleFilterChange} size="small" />
              <TextField label="From" name="from" type="date" value={filters.from} onChange={handleFilterChange} size="small" InputLabelProps={{ shrink: true }} />
              <TextField label="To" name="to" type="date" value={filters.to} onChange={handleFilterChange} size="small" InputLabelProps={{ shrink: true }} />
            </Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Partners List</Typography>
            {loading ? <CircularProgress /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {partners.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.type}</TableCell>
                      <TableCell>{p.company}</TableCell>
                      <TableCell>{p.contact}</TableCell>
                      <TableCell>{p.email}</TableCell>
                      <TableCell>{p.notes}</TableCell>
                      <TableCell>{new Date(p.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => handleEdit(p)} startIcon={<EditIcon />}>Edit</Button>
                        <Button size="small" color="error" onClick={() => handleDelete(p.id)} startIcon={<DeleteIcon />}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Partner' : 'Add Partner'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} size="small" required />
            <TextField label="Type" name="type" value={form.type} onChange={handleChange} size="small" select>
              {PARTNER_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField label="Company" name="company" value={form.company} onChange={handleChange} size="small" />
            <TextField label="Contact" name="contact" value={form.contact} onChange={handleChange} size="small" />
            <TextField label="Email" name="email" value={form.email} onChange={handleChange} size="small" />
            <TextField label="Notes" name="notes" value={form.notes} onChange={handleChange} size="small" multiline rows={2} />
            <Button type="submit" variant="contained">{editingId ? 'Update' : 'Add'} Partner</Button>
            {editingId && <Button onClick={() => { setForm(emptyForm); setEditingId(null); setDialogOpen(false); }} color="secondary">Cancel</Button>}
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setError('')} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccess('')} severity="success">{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Partners; 